import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { SecurityStatusResponse } from '@luacentrix/api-client';
import type { ApiLanguage } from '@luacentrix/shared';
import { Icon } from '@luacentrix/icons';
import { Alert, Badge, Button, CopyButton, type BadgeTone, Row, StatusIndicator } from '@luacentrix/ui';
import { useApp } from '../app-state';
import { QueryBoundary } from '../components/QueryBoundary';
import { formatDuration, formatTimestamp, remainingMillis } from '../data/format';
import { useSecurityStatus } from '../data/queries';

const PAGE_TITLE = 'Access restricted · LuaCentrix';

const FALLBACK_TITLE = 'Access restricted';

const FALLBACK_DESCRIPTION =
  'Access from this connection is temporarily restricted by LuaCentrix automated protection. This is not a license problem and it does not affect your account data.';

const STATE_LABELS: Record<SecurityStatusResponse['status'], string> = {
  active: 'Restriction active',
  expired: 'Restriction expired',
  revoked: 'Restriction lifted',
  unknown: 'Status not confirmed',
};

const STATE_TONES: Record<SecurityStatusResponse['status'], BadgeTone> = {
  active: 'danger',
  expired: 'neutral',
  revoked: 'success',
  unknown: 'neutral',
};

function detailState(status: SecurityStatusResponse, language: ApiLanguage): string {
  const remaining = remainingMillis(status.expires_at, Date.now());
  if (status.kind === 'permanent') {
    return 'This restriction has no automatic expiry and is under administrator review.';
  }
  if (status.expires_at === null) {
    return 'The service did not supply an expiry for this restriction.';
  }
  const absolute = formatTimestamp(status.expires_at, language);
  if (status.status !== 'active' || remaining === null) {
    return `Recorded expiry ${absolute}.`;
  }
  return `Recorded expiry ${absolute} · about ${formatDuration(remaining, language)} remaining.`;
}

export function BlockedPage() {
  const { api, language } = useApp();
  const [params] = useSearchParams();
  const reference = params.get('ref') ?? '';
  const status = useSecurityStatus(api, reference);

  useEffect(() => {
    const previous = document.title;
    document.title = PAGE_TITLE;
    return () => {
      document.title = previous;
    };
  }, []);

  const copy = status.data?.block_page ?? null;
  const title = copy?.title ?? FALLBACK_TITLE;
  const description = copy?.description ?? FALLBACK_DESCRIPTION;

  return (
    <div className="blocked">
      <header className="blocked__bar">
        <span className="blocked__brand">LuaCentrix</span>
        <span className="blocked__brand-meta">by Centrix Hub</span>
      </header>

      <main className="blocked__main">
        <h1 className="blocked__title">{title}</h1>
        <p className="blocked__lede">{description}</p>

        {reference.length === 0 ? (
          <Alert tone="warning" title="No restriction reference supplied">
            This page only describes a restriction addressed by a reference. Open the link you were
            given, or contact support so they can look the restriction up for you.
          </Alert>
        ) : (
          <div className="blocked__reference-block">
            <span className="blocked__label">Restriction reference</span>
            <Row align="start" className="blocked__reference-row">
              <code className="blocked__reference">{reference}</code>
              <CopyButton value={reference} label={`Copy restriction reference ${reference}`} />
            </Row>
          </div>
        )}

        {reference.length === 0 ? null : (
          <section className="blocked__details" aria-labelledby="blocked-details-title">
            <h2 id="blocked-details-title" className="blocked__section-title">
              Restriction details
            </h2>
            <QueryBoundary
              isPending={status.isPending}
              isError={status.isError}
              error={status.error}
              language={language}
              onRetry={() => void status.refetch()}
              retrying={status.isFetching}
              rows={3}
            >
              {status.data === undefined ? null : (
                <dl className="blocked__facts">
                  <div className="blocked__fact">
                    <dt className="blocked__label">Status</dt>
                    <dd>
                      <Badge tone={STATE_TONES[status.data.status]}>
                        {STATE_LABELS[status.data.status]}
                      </Badge>
                    </dd>
                  </div>
                  <div className="blocked__fact">
                    <dt className="blocked__label">Type</dt>
                    <dd>
                      {status.data.kind === null
                        ? 'Not supplied by the service'
                        : status.data.kind === 'permanent'
                          ? 'Permanent review'
                          : 'Temporary'}
                    </dd>
                  </div>
                  <div className="blocked__fact">
                    <dt className="blocked__label">Expiry</dt>
                    <dd>{detailState(status.data, language)}</dd>
                  </div>
                  {status.data.reason_text === null ? null : (
                    <div className="blocked__fact">
                      <dt className="blocked__label">Recorded reason</dt>
                      <dd>{status.data.reason_text}</dd>
                    </div>
                  )}
                </dl>
              )}
            </QueryBoundary>
            {status.isError ? (
              <p className="blocked__honest">
                LuaCentrix could not confirm the restriction details for this reference. The
                reference above is exactly what the service was asked about, and nothing on this
                page is estimated or invented.
              </p>
            ) : null}
          </section>
        )}

        <section className="blocked__support" aria-labelledby="blocked-support-title">
          <h2 id="blocked-support-title" className="blocked__section-title">
            If this is a mistake
          </h2>
          <p className="blocked__support-note">
            Quote the reference above when contacting support. Restrictions are reviewed by the
            organisation that issued them.
          </p>
          {status.isPending ? (
            <StatusIndicator state="pending" label="Checking the restriction service" />
          ) : copy === null ? (
            <StatusIndicator state="unavailable" label="Support contact not supplied" />
          ) : (
            <>
              <Row className="blocked__support-actions">
                <a
                  className="blocked__support-link blocked__support-link--primary"
                  href={copy.support_url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Icon name="external" size="sm" />
                  <span>Open support</span>
                </a>
                {copy.support_email === undefined || copy.support_email === '' ? null : (
                  <a
                    className="blocked__support-link"
                    href={`mailto:${copy.support_email}`}
                  >
                    <Icon name="mail" size="sm" />
                    <span>{copy.support_email}</span>
                  </a>
                )}
              </Row>
              <p className="blocked__message">
                {status.data?.kind === 'permanent'
                  ? copy.message_permanent
                  : copy.message_temporary}
              </p>
            </>
          )}
        </section>
      </main>

      <footer className="blocked__footer">
        <span>LuaCentrix by Centrix Hub</span>
        <span className="blocked__footer-meta">
          This page never redirects. It only reports what the restriction service confirms.
        </span>
        {status.isError || reference.length === 0 ? null : (
          <Button icon="refresh" loading={status.isFetching} onClick={() => void status.refetch()}>
            Re-check
          </Button>
        )}
      </footer>
    </div>
  );
}
